import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { BaseApiService } from "app/services/base-api.service";
import { catchError, finalize, Observable, of, retry, shareReplay, tap, throwError, timer } from "rxjs";
import { Advertiser } from "./interfaces/advertiser.interface";

@Injectable({
    providedIn: "root",
})
export class AdvertisersService extends BaseApiService<Advertiser> {
    protected override readonly URL: string = "api/v1/advertiser";
    private static readonly LIST_CACHE_TTL_MS: number = 60000;
    private static readonly RETRY_ATTEMPTS_FOR_429: number = 2;
    private static readonly RETRY_BASE_DELAY_MS: number = 800;
    private static readonly LIST_429_COOLDOWN_MS: number = 30000;

    private _listCache: Advertiser[] = [];
    private _listCacheTimestamp: number = 0;
    private _listInFlight$: Observable<Advertiser[]> | null = null;
    private _listRetryAfter: number = 0;

    // List all advertisers
    list(forceReload: boolean = false): Observable<Advertiser[]> {
        const now = Date.now();
        const hasFreshCache =
            this._listCache.length > 0 &&
            now - this._listCacheTimestamp < AdvertisersService.LIST_CACHE_TTL_MS;

        if (!forceReload && hasFreshCache) {
            return of(this._listCache);
        }

        if (!forceReload && this._listRetryAfter > now) {
            return of(this._listCache);
        }

        if (this._listInFlight$) {
            return this._listInFlight$;
        }

        this._listInFlight$ = this._httpClient
            .get<Advertiser[]>(`${this.URL}`)
            .pipe(
                this.retry429WithBackoff<Advertiser[]>(),
                tap((advertisers) => {
                    this._listCache = advertisers;
                    this._listCacheTimestamp = Date.now();
                    this._listRetryAfter = 0;
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error?.status === 429) {
                        this._listRetryAfter = Date.now() + AdvertisersService.LIST_429_COOLDOWN_MS;
                    }
                    return throwError(() => error);
                }),
                finalize(() => {
                    this._listInFlight$ = null;
                }),
                shareReplay(1)
            );

        return this._listInFlight$;
    }

    // List advertisers filtered by agency
    listByAgency(agencyId: string): Observable<Advertiser[]> {
        return this._httpClient.get<Advertiser[]>(`${this.URL}?agency_id=${agencyId}`);
    }

    /**
     * Invalidate the in-memory cache so the next `.list()` call hits the
     * network. Use it when the underlying scope changes (e.g. DSP filter
     * toggled in the campaign wizard) and we want to be sure the next read
     * is not served from a stale cache.
     */
    invalidateListCache(): void {
        this._listCache = [];
        this._listCacheTimestamp = 0;
        this._listInFlight$ = null;
        this._listRetryAfter = 0;
    }

    private retry429WithBackoff<T>() {
        return (source: Observable<T>): Observable<T> =>
            source.pipe(
                retry({
                    count: AdvertisersService.RETRY_ATTEMPTS_FOR_429,
                    delay: (error: HttpErrorResponse, retryCount: number) =>
                        error?.status === 429
                            ? timer(AdvertisersService.RETRY_BASE_DELAY_MS * retryCount)
                            : throwError(() => error),
                })
            );
    }

    getAdvertiser(id: string): Observable<Advertiser> {
        return this._httpClient.get<Advertiser>(`${this.URL}/${id}`);
    }

    createAdvertiser(formData: FormData): Observable<Advertiser> {
        return this._httpClient
            .post<Advertiser>(this.URL, formData)
            .pipe(tap(() => this.invalidateListCache()));
    }

    updateAdvertiser(id: string, formData: FormData): Observable<Advertiser> {
        // Use POST with _method: PUT in FormData for method spoofing
        return this._httpClient.post<Advertiser>(`${this.URL}/${id}`, formData);
    }

    deleteAdvertiser(id: string): Observable<void> {
        return this._httpClient
            .delete<void>(`${this.URL}/${id}`)
            .pipe(tap(() => this.invalidateListCache()));
    }
}
